import { CheckoutPaymentStatusResult, StripeAccount } from 'hydra-data-types';
import { waitForSeconds } from 'ts-utils';
import { ApiClient } from '../api/ApiClient';
import { ActionType } from '../store/ActionTypes';
import { PricingSelector } from '../store/pricing/PricingActions';
import { store as defaultStore, StoreStateInternal, StoreType } from '../store/Store';
import { Timer } from '../util/Timer';
import { UserFriendlyError } from '../util/UserFriendlyError';

const POLL_INTERVAL_SECONDS = 2;
const PAYMENT_STATUS_TIMEOUT_SECONDS = 120;

const STANDARD_ERROR_MESSAGE =
  'Something went wrong while processing your payment. Please check your email for confirmation, and contact customer support if the problem persists.';

export async function finalisePendingPayment(
  storeOverride?: Pick<StoreType, 'getState' | 'dispatch'>
): Promise<{ didFinalisePayment: boolean }> {
  const store = storeOverride ?? defaultStore;
  const state = store.getState();
  const checkoutId = state.purchase.intent.checkoutId;
  const stripeAccount = state.purchase.intent.stripeAccount;

  if (!state.purchase.payment.loading || checkoutId === undefined || stripeAccount === undefined) {
    return { didFinalisePayment: false };
  }

  const result = await checkoutPaymentStatus(checkoutId, stripeAccount);
  dispatchPaymentResult(result, state, store);

  return { didFinalisePayment: result.status === 'complete' };
}

function dispatchPaymentResult(
  paymentStatus: CheckoutPaymentStatusResult,
  state: StoreStateInternal,
  store: Pick<StoreType, 'getState' | 'dispatch'>
): void {
  const checkoutId = state.purchase.intent.checkoutId;
  const { pricing } = state;
  const prices = PricingSelector.prices(state);

  if (paymentStatus.status === 'complete') {
    if (checkoutId === undefined) {
      return;
    }
    store.dispatch({
      type: ActionType.PAYMENT_SUCCESS,
      result: {
        currency: pricing.currency,
        couponCode: pricing.couponCode,
        isRecurring: prices?.isRecurring ?? true,
        paymentMethod: paymentStatus.paymentMethod,
        initialPlanPrice: prices?.initialPlanPrice.value ?? 0,
        checkoutId,
      },
    });
  } else {
    store.dispatch({
      type: ActionType.PAYMENT_FAILURE,
      error: new UserFriendlyError(paymentStatus.error ?? STANDARD_ERROR_MESSAGE),
      paymentMethod: paymentStatus.paymentMethod,
    });
  }
}

/** Polls checkoutPaymentStatus until the payment is complete or error, or timeout. */
async function checkoutPaymentStatus(
  checkoutId: string,
  stripeAccount: StripeAccount
): Promise<CheckoutPaymentStatusResult> {
  const api = new ApiClient();
  const timer = new Timer();

  do {
    const result = await api.checkoutPaymentStatus({ checkoutId, stripeAccount });
    if (result.status !== 'processing') return result;
    await waitForSeconds(POLL_INTERVAL_SECONDS);
  } while (timer.secondsElapsed() < PAYMENT_STATUS_TIMEOUT_SECONDS);

  return {
    paymentMethod: 'unknown',
    status: 'error',
    error:
      'Payment is taking longer than expected. Please check your email or account for confirmation, and contact customer support if the problem persists.',
    success: true,
  };
}
